import { Controller } from "@hotwired/stimulus";
import { v4 as uuid } from "uuid";

export default class extends Controller<HTMLFieldSetElement> {
  static targets = [
    "target",
    "template",
    "newLabelForm",
    "newLabelError",
    "newLabelButton",
    "labelList",
    "infoPane",
    "addButton",
  ];

  static values = { rowLimit: Number };

  declare templateTarget: HTMLElement;
  declare targetTarget: HTMLElement;
  declare newLabelFormTarget: HTMLFormElement;
  declare newLabelErrorTarget: HTMLParagraphElement;
  declare newLabelButtonTarget: HTMLButtonElement;
  declare labelListTarget: HTMLElement;
  declare hasAddButtonTarget: boolean;
  declare addButtonTarget: HTMLButtonElement;
  declare rowLimitValue: number;

  connect(): void {
    this.toggleAddButtonState();
  }

  add(evt: Event): void {
    evt.preventDefault();

    const content: string = this.templateTarget.innerHTML.replace(/RECORD_REF/g, uuid());

    this.targetTarget.insertAdjacentHTML("beforebegin", content);

    this.toggleAddButtonState();
  }

  remove(evt: Event): void {
    evt.preventDefault();

    if (!(evt.target instanceof HTMLElement)) {
      return;
    }

    const wrapper: HTMLElement | null = evt.target.closest("[data-wrapper]");
    if (!wrapper) {
      return;
    }

    const inputs: NodeListOf<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> =
      wrapper.querySelectorAll('input:not([type="hidden"]), textarea, select');
    inputs.forEach((input) => {
      input.disabled = true;
    });

    if (wrapper.dataset.newRecord === "true") {
      wrapper.remove();
    } else {
      wrapper.style.display = "none";
      // Mark the wrapper as destroyed so it doesn't count towards the row limit
      wrapper.setAttribute("data-destroyed", "true");

      const input: HTMLInputElement | null = wrapper?.querySelector("input[name*='_destroy']");
      if (input) {
        input.value = "1";
      }
    }

    this.toggleAddButtonState();
  }

  toggleAddButtonState() {
    if (!this.hasAddButtonTarget || this.rowLimitValue < 1) {
      return;
    }

    const currentRowCount = this.element.querySelectorAll(
      "[data-wrapper]:not([data-destroyed])",
    ).length;

    if (currentRowCount >= this.rowLimitValue) {
      this.addButtonTarget.disabled = true;
    } else {
      this.addButtonTarget.disabled = false;
    }
  }

  showNewLabelForm(): void {
    this.newLabelFormTarget.classList.remove("hidden");
    this.labelListTarget.classList.add("hidden");
  }

  async submitNewLabel(event: SubmitEvent): Promise<void> {
    event.preventDefault();

    const form = this.newLabelFormTarget;

    if (!form) {
      return;
    }

    const submitButton = event.submitter as HTMLButtonElement;
    const errorMsg =
      "There was an error creating the label, please check your label name and try again.";

    this.newLabelErrorTarget.textContent = "";
    submitButton.disabled = true;

    try {
      const response = await fetch(form.action, {
        method: form.method,
        body: new FormData(form),
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": window._csrf.token,
        },
      });

      const data = await response.json();

      if (response.ok) {
        // Trigger the workflow action by programmatically clicking a hidden label button.
        // This approach is a bit hacky but it avoids manually replicating the
        // PATCH request logic inside this controller.
        if (this.newLabelButtonTarget && data.uuid) {
          this.newLabelButtonTarget.value = data.uuid;
          this.newLabelButtonTarget.click();
        }
      } else {
        this.newLabelErrorTarget.textContent = errorMsg;
      }
    } catch {
      this.newLabelErrorTarget.textContent = errorMsg;
    }

    submitButton.disabled = false;
  }
}
